/**
 * Transcript Display Component
 * Displays the complete web version of the student transcript
 * Includes: classes, projects, competencies, certificates, time logs, and graduation requirements
 */

import React, { Suspense, lazy } from 'react';
import { groupPostsByGrade, getScoreClass } from './viewUtils';
import { SkillAveragesGrid } from './SkillAveragesGrid';
import ComponentLoader from './components/ComponentLoader';

// Lazy load transcript display components
const StudentClassesDisplay = lazy(() => import('./ClassManagementComponents').then(m => ({ default: m.StudentClassesDisplay })));
const CertificateTranscriptDisplay = lazy(() => import('./CertificateComponents').then(m => ({ default: m.CertificateTranscriptDisplay })));
const TimeLogTranscriptDisplay = lazy(() => import('./TimeLoggingComponents').then(m => ({ default: m.TimeLogTranscriptDisplay })));
const GraduationRequirementsTranscriptDisplay = lazy(() => import('./GraduationRequirementsComponents').then(m => ({ default: m.GraduationRequirementsTranscriptDisplay })));

/**
 * Displays the complete transcript for a student
 * @param {Object} props - Component props
 * @param {string} props.studentId - The student's user ID
 * @param {Array} props.posts - Array of all posts
 * @param {Array} props.tagFeedbacks - Array of all tag feedbacks
 * @param {Array} props.tags - Array of all skill tags
 * @param {Array} props.studentClasses - Array of student's classes
 * @param {Array} props.classTypes - Array of class types
 * @param {Array} props.certificates - Array of all certificates
 * @param {Array} props.timeLogs - Array of all time logs
 * @param {Object} props.currentUser - Current user object
 * @param {Array} props.graduationRequirements - Array of graduation requirements (optional, for teacher view)
 * @param {Object} props.requirementTypes - Graduation requirement types (optional, for teacher view)
 * @param {Function} props.consolidateTimeLogs - Function to consolidate time logs
 * @param {Function} props.groupTimeLogsByGrade - Function to group time logs by grade
 * @param {boolean} props.showGraduationRequirements - Whether to show graduation requirements section (default: false)
 * @param {boolean} props.showOnlyFeatured - Whether to show only featured posts (default: false)
 */
export const TranscriptProjectsSection = ({
  studentId,
  posts,
  tagFeedbacks,
  tags,
  studentClasses,
  classTypes,
  certificates = [],
  timeLogs = [],
  currentUser,
  graduationRequirements = [],
  requirementTypes = {},
  consolidateTimeLogs,
  groupTimeLogsByGrade,
  showGraduationRequirements = false,
  showOnlyFeatured = false
}) => {
  const studentPosts = posts.filter(post => {
    const isStudentPost = post.userId === studentId;
    const isNotHidden = !post.hideFromTranscript;
    const isFeatured = showOnlyFeatured ? post.featured : true; // If showOnlyFeatured is true, check featured; otherwise show all
    return isStudentPost && isNotHidden && isFeatured;
  });
  
  // Group posts by grade
  const groupedPosts = groupPostsByGrade(studentPosts);
  
  return (
    <>
      {/* Classes Section */}
      <Suspense fallback={<ComponentLoader />}>
        <StudentClassesDisplay
          studentClasses={studentClasses}
          classTypes={classTypes}
          currentUser={currentUser}
          selectedStudentUid={studentId}
        />
      </Suspense>
      
      {/* Projects Section */}
      <div className="projects-container">
        <h3 className="transcript-title">Projects</h3>
        <div className="projects-content">
          {studentPosts.length === 0 ? (
            <p className="no-posts no-posts-padding">No posts yet.</p>
          ) : (
            <div>
              {groupedPosts.map(({ grade, posts: gradePosts }) => (
                <div key={grade} className="transcript-grade-section">
                  <h3 className="transcript-grade-header">
                    {grade === 'Unknown' ? 'Unknown Grade' : grade === 'Portfolio Defense' ? 'Portfolio Defense' : `${grade}th Grade`}
                  </h3>
                  
                  {gradePosts.map(post => {
                    const postFeedbacks = tagFeedbacks.filter(tf => tf.postId === post.id);
                    const postLinks = post.links || [];
                    
                    return (
                      <div key={post.id} className={`transcript-post-card ${post.featured ? 'featured' : ''}`}>
                        {post.featured && (
                          <div className="featured-badge-corner">
                            <i className="fa-solid fa-star"></i> Featured
                          </div>
                        )}
                        
                        {/* Post Title */}
                        <h4 className="transcript-post-title">
                          {post.title}
                        </h4>
                        
                        {/* Project Type */}
                        {post.projectType && (
                          <div className="transcript-project-type">
                            {post.projectType === 'individual' ? 'Individual Project' : 'Group Project'}
                          </div>
                        )}
                        
                        {/* Associated Class */}
                        {post.associatedClass && (
                          <div className="transcript-associated-class">
                            {(() => {
                              const classItem = studentClasses.find(c => c.id === post.associatedClass);
                              const classType = classTypes.find(ct => ct.id === classItem?.classTypeId);
                              return (
                                <>
                                  <i className={`fa-solid ${classType?.icon || 'fa-graduation-cap'}`}></i>
                                  {classItem ? `${classItem.className} - ${classItem.grade}th Grade` : 'Unknown Class'}
                                </>
                              );
                            })()}
                          </div>
                        )}
                        
                        {/* Project Description */}
                        {post.projectDescription && (
                          <div className="transcript-project-description">
                            <p className="transcript-description-text">{post.projectDescription}</p>
                          </div>
                        )}
                        
                        {/* Links */}
                        {postLinks.length > 0 && (
                          <div className="transcript-links-section">
                            <h5 className="transcript-section-header">
                              Links:
                            </h5>
                            <div className="transcript-links-container">
                              {postLinks.map((link, index) => (
                                <div key={index} className="transcript-link-item">
                                  <i className="fa-solid fa-link transcript-link-icon"></i>
                                  <a 
                                    href={link.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="transcript-link"
                                  >
                                    {link.title}
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Skill Scores */}
                        {postFeedbacks.length > 0 && (
                          <div>
                            <h5 className="transcript-section-header">
                              Skill Scores:
                            </h5>
                            <div className="transcript-scores-container">
                              {postFeedbacks.map((feedback, index) => (
                                <div key={index} className="transcript-score-item">
                                  <span className="transcript-score-skill">
                                    {feedback.tag}:
                                  </span>
                                  <span className={`transcript-score-value ${getScoreClass(feedback.score)}`}>
                                    {typeof feedback.score === 'number' ? `${feedback.score}` : 'No Score'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Post Date */}
                        <div className="transcript-post-date">
                          Submitted: {new Date(post.timestamp).toLocaleDateString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Competencies Summary */}
      <SkillAveragesGrid
        studentId={studentId}
        tags={tags}
        tagFeedbacks={tagFeedbacks}
      />
      
      {/* Certificates Section */}
      <div className="time-logs-transcript-container">
        <h3 className="transcript-title">
          Certificates & Awards
        </h3>
        <div className="time-logs-transcript-content">
          <Suspense fallback={<ComponentLoader />}>
            <CertificateTranscriptDisplay
              certificates={certificates}
              currentUser={currentUser}
              studentId={studentId}
            />
          </Suspense>
        </div>
      </div>
      
      {/* Time Logs Section */}
      <div className="time-logs-transcript-container">
        <h3 className="transcript-title">
          Logged Activities
        </h3>
        <div className="time-logs-transcript-content">
          <Suspense fallback={<ComponentLoader />}>
            <TimeLogTranscriptDisplay
              timeLogs={timeLogs}
              currentUser={currentUser}
              studentId={studentId}
              consolidateTimeLogs={consolidateTimeLogs}
              groupTimeLogsByGrade={groupTimeLogsByGrade}
            />
          </Suspense>
        </div>
      </div>
      
      {/* Graduation Requirements Section (for teacher view) */}
      {showGraduationRequirements && (
        <div className="time-logs-transcript-container">
          <h3 className="transcript-title">
            Graduation Requirements
          </h3>
          <div className="time-logs-transcript-content">
            <Suspense fallback={<ComponentLoader />}>
              <GraduationRequirementsTranscriptDisplay
                graduationRequirements={graduationRequirements}
                selectedStudentUid={studentId}
                requirementTypes={requirementTypes}
              />
            </Suspense>
          </div>
        </div>
      )}
    </>
  );
};

